import type { GameLike } from './GameCard.js';
import { isLotteryGame } from './lotteryArt.js';
import { formatBangkokTime } from '../../lib/datetime/bangkok.js';

/**
 * Three-state lottery UI status.
 *
 * - `open`     — `lotteryStatus` is AWAITING/OPEN and the countdown to
 *                 `ticketBefore` hasn't reached zero. Show countdown + bet CTA.
 * - `closed`   — `lotteryStatus` is CLOSED (provider explicitly said so).
 *                 Show grey pill + next-round CTA.
 * - `syncing`  — `lotteryStatus` is AWAITING/OPEN but our cached
 *                 `ticketBefore` is already in the past. Almost always means
 *                 our catalog snapshot is stale and the auto-sync + lobby
 *                 polling will overwrite it within ~60s. Show a "refreshing"
 *                 pill so the card doesn't look broken to the player.
 */
export type LotteryState = 'open' | 'closed' | 'syncing';

export function lotteryState(game: GameLike, expired: boolean): LotteryState {
  const status = (game.lotteryStatus ?? '').toUpperCase();
  if (status === 'CLOSED') return 'closed';
  if (status === 'AWAITING' || status === 'OPEN') return expired ? 'syncing' : 'open';
  // No known status — infer from the countdown alone.
  return expired ? 'closed' : 'open';
}

/** Back-compat helper — true only for the `open` state. */
export function isLotteryOpen(game: GameLike, expired: boolean): boolean {
  return lotteryState(game, expired) === 'open';
}

/**
 * i18n key for the play button:
 *   non-lottery        → 'common.play'        ("เล่น")
 *   lottery, open      → 'lottery.bet'        ("แทงหวย")
 *   lottery, otherwise → 'lottery.nextRound'  ("ดูรอบถัดไป")
 *
 * `syncing` falls into the otherwise branch deliberately — we don't know if
 * the round is actually still open upstream, so directing the player to the
 * next round is the safer default. Polling refreshes the card within a minute.
 */
export function playLabelKey(game: GameLike, open: boolean): string {
  if (!isLotteryGame(game)) return 'common.play';
  return open ? 'lottery.bet' : 'lottery.nextRound';
}

export interface LotteryTimeParts {
  openAt: string; // "HH:mm" Bangkok, or '' if missing
  closeAt: string; // "HH:mm" Bangkok, or '' if missing
}

/** Open/close clock times for a lottery round, formatted in Bangkok time. */
export function lotteryTimeParts(game: GameLike): LotteryTimeParts {
  return {
    openAt: formatBangkokTime(game.ticketAfter ?? null),
    closeAt: formatBangkokTime(game.ticketBefore ?? null),
  };
}
